import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, BookOpen, Trophy, Users, CheckCircle2, ArrowRight, Mail, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForInstitutes = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    institute: '',
    role: '',
    students_count: '',
    interest: 'question_bank',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.institute.trim()) {
      setError('Name, email and institute are required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/institute`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="for-institutes-page">
      {/* Hero */}
      <section className="relative px-6 py-20 overflow-hidden bg-gradient-to-br from-[#0EA5E9] via-[#0284C7] to-[#0C4A6E] text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FBBF24]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto text-center">
          <Link to="/" className="inline-block mb-8 opacity-90 hover:opacity-100 transition">
            <img
              src="/aptivon-logo-v2.png"
              alt="Aptivon"
              className="h-14 w-auto mx-auto drop-shadow-2xl"
            />
          </Link>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-white/15 backdrop-blur-sm border border-white/20 mb-6">
            For Coaching Institutes &amp; Schools
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="institutes-hero-title"
          >
            Power your students with <span className="text-[#FBBF24]">AI-graded JEE &amp; NEET</span> mock tests
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            White-label our 10,000+ AI-generated question bank, anti-cheat live quiz infrastructure, and rank-prediction analytics for your institute — at a fraction of building it yourself.
          </p>

          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <a
              href="#contact"
              className="px-8 py-4 bg-[#FBBF24] text-[#111827] rounded-2xl font-bold shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition-all text-lg flex items-center gap-2"
              data-testid="institutes-cta-talk-to-sales"
            >
              Talk to sales <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="mailto:partnerships@aptivon.in"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl font-bold hover:bg-white/20 transition text-lg flex items-center gap-2"
              data-testid="institutes-cta-email"
            >
              <Mail className="w-5 h-5" /> Email us
            </a>
          </div>
        </div>
      </section>

      {/* What we offer */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-center text-[#111827] mb-3"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            What we offer to institutes
          </h2>
          <p className="text-base text-[#6B7280] text-center max-w-2xl mx-auto mb-14">
            Three flexible partnership models. Pick what fits your scale.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                Icon: BookOpen,
                title: 'Question Bank Licensing',
                bullets: [
                  '10,000+ JEE & NEET questions',
                  'Topic-tagged, AI-curated, exam-grade',
                  'CSV / API delivery',
                  'Custom subjects on request',
                ],
                price: 'From ₹50K / subject',
                color: 'from-emerald-500 to-green-600',
                badge: 'Most popular',
              },
              {
                Icon: Trophy,
                title: 'Sponsored Scholarship Tests',
                bullets: [
                  'Co-branded mock contest on Aptivon',
                  'Access to top performers as warm leads',
                  'Anti-cheat & live leaderboard included',
                  'You fund the prizes, we drive participation',
                ],
                price: '₹2L–25L / contest',
                color: 'from-amber-500 to-orange-600',
                badge: 'High ROI',
              },
              {
                Icon: Building2,
                title: 'White-Label Platform',
                bullets: [
                  'Your branding, our infrastructure',
                  'Custom subdomain (eg. quiz.allen.ac.in)',
                  'Dedicated admin dashboard',
                  'SLA-backed uptime',
                ],
                price: 'Custom pricing',
                color: 'from-purple-500 to-fuchsia-600',
                badge: 'Enterprise',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="relative rounded-3xl p-7 bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`institute-offer-${card.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className={`absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${card.color} shadow-md`}>
                  {card.badge}
                </span>
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4`}>
                  <card.Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {card.title}
                </h3>
                <ul className="space-y-2 mb-5">
                  {card.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#6B7280]">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm font-bold text-[#0EA5E9]">{card.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Aptivon stats */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: '10K+', l: 'Aspirants on platform' },
            { v: '500+', l: 'Live quiz pools / month' },
            { v: '<2%', l: 'Cheating rate (anti-cheat AI)' },
            { v: '24×7', l: 'Quiz availability' },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="text-4xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {s.v}
              </p>
              <p className="text-sm text-[#6B7280] mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Get a partnership proposal
            </h2>
            <p className="text-[#6B7280] mt-2">We'll get back to you within 1 business day.</p>
          </div>

          {submitted ? (
            <div className="rounded-3xl p-10 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 text-center" data-testid="institute-form-success">
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600 mb-3" />
              <h3 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Got it! Thanks {form.name.split(' ')[0]}.
              </h3>
              <p className="text-[#6B7280] mt-2">
                Our partnerships team will reach out at <strong>{form.email}</strong> within 1 business day.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-[#0EA5E9] font-bold hover:underline"
              >
                Back to home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="rounded-3xl p-8 bg-white border border-slate-100 shadow-lg space-y-4" data-testid="institute-form">
              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600" data-testid="institute-form-error">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Your name *" value={form.name} onChange={update('name')} testid="institute-input-name" placeholder="Dr. Anil Sharma" />
                <Input label="Work email *" type="email" value={form.email} onChange={update('email')} testid="institute-input-email" placeholder="anil@institute.in" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Institute / school *" value={form.institute} onChange={update('institute')} testid="institute-input-institute" placeholder="Allen Career Institute" />
                <Input label="Your role" value={form.role} onChange={update('role')} testid="institute-input-role" placeholder="Director / Academic Head" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#111827] mb-2">Approx. students</label>
                  <select
                    value={form.students_count}
                    onChange={update('students_count')}
                    className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors text-[#111827]"
                    data-testid="institute-input-students"
                  >
                    <option value="">Select range</option>
                    <option value="<100">Less than 100</option>
                    <option value="100-500">100 – 500</option>
                    <option value="500-2000">500 – 2,000</option>
                    <option value="2000-10000">2,000 – 10,000</option>
                    <option value=">10000">10,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#111827] mb-2">Primary interest</label>
                  <select
                    value={form.interest}
                    onChange={update('interest')}
                    className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors text-[#111827]"
                    data-testid="institute-input-interest"
                  >
                    <option value="question_bank">Question bank licensing</option>
                    <option value="sponsorship">Sponsored scholarship test</option>
                    <option value="white_label">White-label platform</option>
                    <option value="other">Something else</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#111827] mb-2">Tell us about your need</label>
                <textarea
                  value={form.message}
                  onChange={update('message')}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors resize-none"
                  placeholder="e.g. We run 50+ test centres across India and need 5,000 NEET-pattern questions for our weekly mocks…"
                  data-testid="institute-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-4 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="institute-form-submit"
              >
                <Send className="w-5 h-5" />
                {submitting ? 'Sending…' : 'Send partnership request'}
              </button>
              <p className="text-xs text-center text-[#9CA3AF]">
                By submitting, you agree to be contacted by Aptivon for partnership-related queries.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111827] text-white py-10 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-block mb-4">
            <img
              src="/aptivon-logo-v2.png"
              alt="Aptivon"
              className="h-10 w-auto mx-auto"
            />
          </Link>
          <div className="flex justify-center gap-6 mb-3 flex-wrap text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition">Home</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition">Terms</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy</Link>
            <a href="mailto:partnerships@aptivon.in" className="text-gray-400 hover:text-white transition">partnerships@aptivon.in</a>
          </div>
          <p className="text-xs text-gray-500">© 2026 Aptivon. Built for institutions that take aspirants seriously.</p>
        </div>
      </footer>
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-sm font-bold text-[#111827] mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
      data-testid={testid}
    />
  </div>
);

export default ForInstitutes;
